/**
 * Custom Select — replaces native <select> with a styled dropdown.
 *
 * Usage: wrap any <select> with <custom-select>:
 *   <custom-select>
 *     <select name="quantity">
 *       <option value="1">1</option>
 *       ...
 *     </select>
 *   </custom-select>
 *
 * The native <select> is hidden but stays in the DOM for form submission.
 * Change events are dispatched on the native <select> so existing on:change handlers work.
 */

class CustomSelect extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #dropdown = null;
  #open = false;
  #onDocClick = this.#handleDocClick.bind(this);
  #onKeydown = this.#handleKeydown.bind(this);

  connectedCallback() {
    this.#select = this.querySelector('select');
    if (!this.#select) return;

    // Guard against double init (e.g. morph re-insert)
    if (this.querySelector('.custom-select__trigger')) {
      this.#trigger = this.querySelector('.custom-select__trigger');
      this.#dropdown = this.querySelector('.custom-select__dropdown');
      return;
    }

    this.#select.style.display = 'none';
    this.#select.setAttribute('tabindex', '-1');
    this.#select.setAttribute('aria-hidden', 'true');

    this.#buildUI();

    this.#select.addEventListener('change', () => {
      this.#syncFromNative();
    });
  }

  disconnectedCallback() {
    this.#closeDropdown();
  }

  #buildUI() {
    const selected = this.#select.options[this.#select.selectedIndex];
    const label = this.getAttribute('data-label');

    // Trigger button
    this.#trigger = document.createElement('button');
    this.#trigger.type = 'button';
    this.#trigger.className = 'custom-select__trigger';
    this.#trigger.setAttribute('aria-haspopup', 'listbox');
    this.#trigger.setAttribute('aria-expanded', 'false');

    if (label) {
      // Label mode: show fixed label with sort icon, dropdown shows the options
      this.#trigger.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 4v16M7 4l-3 3M7 4l3 3M17 20V4M17 20l-3-3M17 20l3-3"/>
        </svg>
        <span class="custom-select__value">${label}</span>
      `;
      this.#trigger.classList.add('custom-select__trigger--label');
    } else {
      this.#trigger.innerHTML = `
        <span class="custom-select__value">${selected ? selected.textContent.trim() : ''}</span>
        <svg class="custom-select__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      `;
    }
    this.#trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#toggle();
    });

    // Dropdown
    this.#dropdown = document.createElement('div');
    this.#dropdown.className = 'custom-select__dropdown';
    this.#dropdown.setAttribute('role', 'listbox');
    this.#buildOptions();

    this.appendChild(this.#trigger);
    this.appendChild(this.#dropdown);
  }

  #buildOptions() {
    this.#dropdown.innerHTML = '';
    for (const option of this.#select.options) {
      const item = document.createElement('div');
      item.className = 'custom-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('data-value', option.value);
      item.textContent = option.textContent.trim();

      if (option.selected) {
        item.classList.add('is-selected');
        item.setAttribute('aria-selected', 'true');
      }

      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#selectOption(option.value);
      });

      this.#dropdown.appendChild(item);
    }
  }

  #selectOption(value) {
    this.#select.value = value;
    this.#select.setAttribute('value', value);

    // Dispatch native change event so on:change and onchange handlers work
    this.#select.dispatchEvent(new Event('change', { bubbles: true }));

    this.#updateSelected(value);
    this.#closeDropdown();
  }

  #updateSelected(value) {
    // In label mode, don't update the trigger text (it stays as the fixed label)
    if (!this.hasAttribute('data-label')) {
      const selected = this.#select.options[this.#select.selectedIndex];
      const valueEl = this.#trigger.querySelector('.custom-select__value');
      if (valueEl && selected) {
        valueEl.textContent = selected.textContent.trim();
      }
    }

    for (const item of this.#dropdown.children) {
      const isSelected = item.getAttribute('data-value') === value;
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    }
  }

  /** Sync custom UI when native <select> changes externally */
  #syncFromNative() {
    const value = this.#select.value;
    this.#updateSelected(value);
    this.#buildOptions();
  }

  #toggle() {
    this.#open ? this.#closeDropdown() : this.#openDropdown();
  }

  #openDropdown() {
    this.#open = true;
    this.classList.add('is-open');
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#dropdown.style.display = 'block';

    // Position: flip up if near bottom
    const rect = this.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = this.#dropdown.scrollHeight;
    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      this.#dropdown.classList.add('custom-select__dropdown--up');
    } else {
      this.#dropdown.classList.remove('custom-select__dropdown--up');
    }

    // Scroll selected into view
    const selected = this.#dropdown.querySelector('.is-selected');
    if (selected) selected.scrollIntoView({ block: 'nearest' });

    document.addEventListener('click', this.#onDocClick, true);
    document.addEventListener('keydown', this.#onKeydown);
  }

  #closeDropdown() {
    this.#open = false;
    this.classList.remove('is-open');
    if (this.#trigger) this.#trigger.setAttribute('aria-expanded', 'false');
    if (this.#dropdown) this.#dropdown.style.display = 'none';

    document.removeEventListener('click', this.#onDocClick, true);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #handleDocClick(e) {
    if (!this.contains(e.target)) {
      this.#closeDropdown();
    }
  }

  #handleKeydown(e) {
    if (e.key === 'Escape') {
      this.#closeDropdown();
      this.#trigger.focus();
      return;
    }

    const options = [...this.#dropdown.children];
    const currentIndex = options.findIndex((o) => o.classList.contains('is-selected'));

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(currentIndex + 1, options.length - 1);
      this.#selectOption(options[next].getAttribute('data-value'));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(currentIndex - 1, 0);
      this.#selectOption(options[prev].getAttribute('data-value'));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.#closeDropdown();
    }
  }
}

customElements.define('custom-select', CustomSelect);
